import { useMemo, useState, type FormEvent } from 'react';

import { IconPrinter, IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { useTableSort } from '../../lib/table-sort';
import { useOpdLookup } from '../opd/opd.queries';
import { todayDateFilters, todayIso } from './report-filters';
import { useDoctorPayoutReport } from './reports.queries';
import type { ReportListFilters } from './types';

const initialFilters = (): ReportListFilters => ({
  page: 1,
  limit: 25,
  ...todayDateFilters(),
  section: 'opd',
  value: '',
});

const amount = (value: number | null | undefined) => (value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const parseIsoDate = (value: string) => new Date(`${value}T00:00:00`);
const longDate = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
const shortDate = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const reportPeriod = (filters: ReportListFilters) => {
  if (filters.fromDate && filters.toDate && filters.fromDate === filters.toDate) {
    const [weekday, date] = longDate.format(parseIsoDate(filters.fromDate)).split(', ');
    return `${weekday} (${date})`;
  }
  if (filters.fromDate && filters.toDate) {
    return `${shortDate.format(parseIsoDate(filters.fromDate))} - ${shortDate.format(parseIsoDate(filters.toDate))}`;
  }
  if (filters.fromDate) return `From ${shortDate.format(parseIsoDate(filters.fromDate))}`;
  if (filters.toDate) return `Through ${shortDate.format(parseIsoDate(filters.toDate))}`;
  return 'All dates';
};

const emptyTotals = () => ({
  patients: 0,
  amount: 0,
  doctorPaid: 0,
  hospital: 0,
});

export const DoctorPayoutReportPage = () => {
  const [draft, setDraft] = useState<ReportListFilters>(initialFilters);
  const [filters, setFilters] = useState<ReportListFilters>(initialFilters);
  const [draftDoctorId, setDraftDoctorId] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const doctors = useOpdLookup('doctors');
  const query = useDoctorPayoutReport(filters);

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters({ ...draft, page: 1, value: '' });
    setDoctorId(draftDoctorId);
  };

  const reset = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
    setDraftDoctorId('');
    setDoctorId('');
    setTableSearch('');
  };

  const visibleDoctors = useMemo(() => {
    const needle = tableSearch.trim().toLocaleLowerCase();
    const doctors = doctorId
      ? (query.data?.summary.byDoctor ?? []).filter((doctor) => doctor.doctorId === doctorId)
      : query.data?.summary.byDoctor ?? [];
    return needle
      ? doctors.filter((doctor) => doctor.doctorName.toLocaleLowerCase().includes(needle))
      : doctors;
  }, [doctorId, query.data?.summary.byDoctor, tableSearch]);

  const doctorSort = useTableSort(visibleDoctors, {
    doctors: (doctor) => doctor.doctorName,
    patients: (doctor) => doctor.patients,
    total: (doctor) => doctor.amount,
    doctorPaid: (doctor) => doctor.doctorPaid,
    hospital: (doctor) => doctor.hospital,
  });

  const totals = useMemo(() => doctorSort.rows.reduce((total, doctor) => {
    total.patients += doctor.patients;
    total.amount += doctor.amount;
    total.doctorPaid += doctor.doctorPaid;
    total.hospital += doctor.hospital;
    return total;
  }, emptyTotals()), [doctorSort.rows]);

  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Doctor payout report could not be loaded.';

  return (
    <div className="page-stack doctor-payout-page">
      <section className="content-card doctor-payout-report-card">
        <header className="doctor-payout-titlebar"><h1>OPD Doctor Payout</h1></header>

        <form className="doctor-payout-filters report-print-hidden" onSubmit={applyFilters}>
          <label>
            <span className="sr-only">Doctor</span>
            <select aria-label="Doctor" value={draftDoctorId} onChange={(event) => setDraftDoctorId(event.target.value)}>
              <option value="">All Doctors</option>
              {(doctors.data ?? []).map((doctor) => <option key={doctor.id} value={doctor.id}>{doctor.label}</option>)}
            </select>
          </label>
          <label>
            <span className="sr-only">From date</span>
            <input
              aria-label="From date"
              type="date"
              value={draft.fromDate ?? ''}
              max={draft.toDate || todayIso()}
              onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))}
            />
          </label>
          <label>
            <span className="sr-only">To date</span>
            <input
              aria-label="To date"
              type="date"
              value={draft.toDate ?? ''}
              min={draft.fromDate || undefined}
              max={todayIso()}
              onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))}
            />
          </label>
          <button className="report-toolbar-btn search" type="submit"><IconSearch />Search</button>
          <button className="report-toolbar-btn reset" type="button" onClick={reset}><IconRefreshCw />Reset</button>
          <button className="report-toolbar-btn print" type="button" onClick={() => window.print()}><IconPrinter />Print</button>
        </form>

        <div className="doctor-payout-table-area">
          <div className="doctor-payout-table-toolbar report-print-hidden">
            <label className="report-table-search">
              Search:
              <input value={tableSearch} onChange={(event) => setTableSearch(event.target.value)} aria-label="Search doctors" />
            </label>
          </div>

          {query.isLoading ? <LoadingState /> : null}
          {query.isError ? <ErrorState title="Unable to load doctor payout" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
          {query.isSuccess && visibleDoctors.length === 0 ? (
            <EmptyState
              title={tableSearch || doctorId ? 'No matching doctors found' : 'No payout rows found'}
              message={tableSearch || doctorId ? 'Try a different doctor.' : 'Change the section or date range and search again.'}
            />
          ) : null}
          {query.isSuccess && visibleDoctors.length > 0 ? (
            <div className="report-table-scroll">
              <table className="doctor-payout-summary-table">
                <thead>
                  <tr className="doctor-payout-period-row"><th colSpan={6}>{reportPeriod(filters)}</th></tr>
                  <tr>
                    <th>#</th>
                    <SortableTh control={doctorSort} column="doctors">Doctors</SortableTh>
                    <SortableTh control={doctorSort} column="patients">Patients</SortableTh>
                    <SortableTh control={doctorSort} column="total">Total</SortableTh>
                    <SortableTh control={doctorSort} column="doctorPaid">Doctor Paid</SortableTh>
                    <SortableTh control={doctorSort} column="hospital">Hospital</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {doctorSort.rows.map((doctor, index) => (
                    <tr key={doctor.doctorId}>
                      <td>{index + 1}</td>
                      <td className="doctor-payout-doctor">{doctor.doctorName}</td>
                      <td>{doctor.patients.toLocaleString('en-IN')}</td>
                      <td>{amount(doctor.amount)}</td>
                      <td>{amount(doctor.doctorPaid)}</td>
                      <td>{amount(doctor.hospital)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>Total</td>
                    <td>{totals.patients.toLocaleString('en-IN')}</td>
                    <td>{amount(totals.amount)}</td>
                    <td>{amount(totals.doctorPaid)}</td>
                    <td>{amount(totals.hospital)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};
